<template>
  <div class="popup-content">
    <i class="icon-alert-triangle popup-icon warning"/>
    <div>{{ getInfoMessage }}</div>
    <div class="popup-footer">
      <button class="cancel" @click="cancelObjectRemove()">
        {{ $t.cancel }}
      </button>
      <button class="primary" @click="acceptObjectRemove()">
        {{ $t.ok }}
      </button>
    </div>
  </div>
</template>

<script>
import SyntheticObjectsService from "@/services/synthetic-objects.service";
import VisualisationService from "@/services/visualisation.service";
import UtilsService from "@/services/utils.service";
import SynodicCalculationService from "@/services/synodic-calculation.service";
import ObjectsService from "@/services/objects.service";
import Config from "@/constants/config";
import SpacekitService from "@/services/spacekit.service";
import SkychartService from "@/services/skychart.service";

export default {
  name: "PopupSyntheticObjectDelete",
  components: {},
  props: {
    config: Object,
  },
  computed: {
    getInfoMessage() {
      let objectName = "";
      if (this.config.removeByObject === Config.removeByObject.database) {
        objectName = this.config.selectedObject.name
      } else {
        objectName = this.config.selectedObject.objectName;
      }
      return this.$t.removeUserDefinedObject.replace("{objectName}", objectName);
    },
  },
  methods: {
    cancelObjectRemove() {
      this.$emit("cancel");
    },

    acceptObjectRemove() {
      // Database objects
      if (this.config.removeByObject === Config.removeByObject.database) {
        this.removeDataBaseObject(this.config.selectedObject);
      }
      // User-defined objects
      if (this.config.removeByObject === Config.removeByObject.userDefined) {
        const objectName = this.config.selectedObject.objectName;
        switch (this.config.objectType) {
            // scd tool
          case  SyntheticObjectsService.OBJECT_TYPES.scd:
            this.removeDefaultObject(objectName);
            break
            // ovt - sovt tool
          case  SyntheticObjectsService.OBJECT_TYPES.hyperbolic:
          case  SyntheticObjectsService.OBJECT_TYPES.elliptical:
            this.removeSyntheticObject(objectName, this.config.objectType);
            break
          default:
            this.objectConfigurationRemove(this.config.selectedObject);
            break;
        }
      }
      this.$emit("submit");
    },

    async removeDataBaseObject(object) {
      // When a data-user object is added from SCD, FVT, OVT and SOVT tool, then to have removed it in every tool,
      // considering any Keplerian/perturbed orbits displayed for removal.

      await this.checkOvtTool(object);
      await this.checkSovtTool(object);
      this.checkFvtTool(object);
      await this.checkSdcTool(object);

      // Remove from updateAsteroidsList
      this.objectConfigurationRemove(object);
      this.$forceUpdate();
    },

    async checkOvtTool(object) {
      // Check OVT tool
      let visibleOvtObjectsList = VisualisationService.getIsChecked();
      let objectVisible = visibleOvtObjectsList.find((item) => item === String(object.number));
      if (objectVisible) {
        visibleOvtObjectsList = visibleOvtObjectsList.filter((item) => item !== String(object.number));
        VisualisationService.setIsChecked(visibleOvtObjectsList);
      }
      let perturbedOrbitObjectsList = VisualisationService.getIsPerturbed();
      let objectPerturbed = perturbedOrbitObjectsList.find((item) => item === String(object.number));
      if (objectPerturbed) {
        perturbedOrbitObjectsList = perturbedOrbitObjectsList.filter((item) => item !== String(object.number));
        await VisualisationService.setIsPerturbed(perturbedOrbitObjectsList);
        VisualisationService.removeObject(object.number + '_perturbed');
        SpacekitService.removeMarkerByName('marker_' + object.number + '_perturbed');
      }
      if (String(object.number) === VisualisationService.getSelectedObjectName()) {
        VisualisationService.deHighlightObject(object.number);
        await VisualisationService.setSelectedObjectName(null);
        VisualisationService.removeObject(object.number);
        SpacekitService.removeMarkerByName('marker_' + object.number);
      }
    },

    async checkSovtTool(object) {
      // Check SVT tool
      const designator = (object.number ? object.number : object.name).toString();
      const visibleSynodicObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
      const objectVisible = visibleSynodicObjectsList.includes(designator);
      if (objectVisible) {
        SynodicCalculationService.checkIsFocusOnObject(designator);
        SynodicCalculationService.removeVisibleCalculatedObject(designator);
      }
      let perturbedSynodicOrbitList = SynodicCalculationService.getPerturbedSynodicOrbitList();
      const objectPerturbed = perturbedSynodicOrbitList.includes(designator);
      if (objectPerturbed) {
        perturbedSynodicOrbitList = perturbedSynodicOrbitList.filter((item) => item !== designator);
        await SynodicCalculationService.setPerturbedSynodicOrbitList(perturbedSynodicOrbitList);
      }
      if (designator === VisualisationService.getSelectedSynodicObjectName()) {
        SynodicCalculationService.deselectVisibleSpaceObject();
        VisualisationService.selectSynodicObject(null)
      }

    },

    async checkSdcTool(object) {
      // Check SCD tool from Visualisation
      let skychartLoadedObjects = SkychartService.getLoadedObjects();
      if (skychartLoadedObjects) {
        skychartLoadedObjects = skychartLoadedObjects.filter(o => o !== object.name);
        SkychartService.setLoadedObjects(skychartLoadedObjects);
        let activeObjectName = SkychartService.getActiveObjectName();
        if (activeObjectName === object.name) {
          SkychartService.setActiveObjectName('');
          SkychartService.setObjectsDetailsHide(true);
        }
        let skychartTraceObjects = SkychartService.getTraceObjects();
        if (skychartTraceObjects) {
          //-----------------------------
          let traceObjectsToRemove = Object.keys(skychartTraceObjects).reduce((acc, key) => {
            if (key === object.name) {
              acc[key] = skychartTraceObjects[key];
            }
            return acc;
          }, {});
          SkychartService.setTraceObjectsToRemove(traceObjectsToRemove);
          //-------------------------------
          skychartTraceObjects = Object.keys(skychartTraceObjects).reduce((acc, key) => {
            if (key !== object.name) {
              acc[key] = skychartTraceObjects[key];
            }
            return acc;
          }, {});
          SkychartService.setTraceObjects(skychartTraceObjects)
          const traceObjects = SkychartService.getTraceObjects();
          if (!Object.keys(traceObjects).length) {
            if (this.$route.path === '/scd/visualisation') {
              try {
                this.$router.push("/scd/setup");
                await SkychartService.setScdRoute("/scd/setup");
                this.$forceUpdate()
              } catch (error) {
                console.error("Error setting route:", error);
              }
            }
          }
        }
      }
    },

    checkFvtTool(object) {
      // Check FVT tool
      // Remove from close approaches list
      let closeApproaches = VisualisationService.getCloseApproaches();
      if (closeApproaches.length > 0) {
        const objectIndex = UtilsService.findItemIndexInObjectList("name", object.name, closeApproaches);
        closeApproaches.splice(objectIndex, 1);
        VisualisationService.setCloseApproaches(closeApproaches);
      }
      // Remove from flyby scene
      if(VisualisationService.getTool() === 'fvt')
        SpacekitService.removeFlybyScene(true);

      // Remove from selected close approach
      let selectedCloseApproach = VisualisationService.getSelectedCloseApproach();
      if (selectedCloseApproach)
        VisualisationService.setSelectedCloseApproach(null);
      // Remove from orbit active object
      let orbitActiveObject = VisualisationService.getOrbitActiveObject();
      if (orbitActiveObject)
        VisualisationService.setOrbitActiveObject(null);

    },

    /**
     * Remove object from the finalAsteroidsList
     * @param object
     */
    objectConfigurationRemove(object) {
      ObjectsService.objectConfigurationRemove([object])
    },

    removeDefaultObject(objectName) {
      const objectsList = UtilsService.deepCopy(SyntheticObjectsService.getSyntheticDefaultObjectsList());
      const objectIndex = UtilsService.findItemIndexInObjectList("objectName", objectName, objectsList);
      objectsList.splice(objectIndex, 1);
      SyntheticObjectsService.setSyntheticDefaultObjectsList(objectsList);
    },

    removeSyntheticObject(objectName, type) {
      if (type === SyntheticObjectsService.OBJECT_TYPES.hyperbolic) {
        const objectsList = UtilsService.deepCopy(SyntheticObjectsService.getHyperbolicObjectsList());
        const objectIndex = UtilsService.findItemIndexInObjectList("objectName", objectName, objectsList);
        objectsList.splice(objectIndex, 1);
        SyntheticObjectsService.setHyperbolicObjectsList(objectsList);
      } else {
        const objectsList = UtilsService.deepCopy(SyntheticObjectsService.getEllipticalObjectsList());
        const objectIndex = UtilsService.findItemIndexInObjectList("objectName", objectName, objectsList);
        objectsList.splice(objectIndex, 1);
        SyntheticObjectsService.setEllipticalObjectsList(objectsList);
      }
      const currentTool = VisualisationService.getTool();
      currentTool === "ovt" && this.adjustOvtToolFeatures();
      currentTool === "sovt" && this.adjustSovtToolFeatures();
    },

    adjustOvtToolFeatures() {
      const {designator} = this.config.selectedObject;
      const { checkedObjectsList, perturbedObjectsList, isObjectChecked, isObjectSelected, isObjectPerturbed } =
          SyntheticObjectsService.getOvtObjectsInformationsByDesignator(designator);
      isObjectSelected && this.adjustOvtSelectedObject();
      isObjectChecked && this.adjustOvtCheckedObjects(checkedObjectsList, designator, isObjectPerturbed);
      isObjectPerturbed && this.adjustOvtPerturbedObject(perturbedObjectsList, designator);
    },

    adjustOvtSelectedObject() {
      VisualisationService.setSelectedObjectName(null);
    },

    adjustOvtCheckedObjects(checkedObjectsList, designator, isObjectPerturbed) {
      const focusedObject = VisualisationService.getOvtFocusedObject();
      const isFocusedDeselected = focusedObject === designator;
      isFocusedDeselected && VisualisationService.setOvtFocusedObject('');
      checkedObjectsList.splice(checkedObjectsList.indexOf(designator), 1);
      VisualisationService.removeObject(`${designator}${isObjectPerturbed ? "_perturbed" : ""}`);
      SpacekitService.removeMarkerByName('marker_' + `${designator}${isObjectPerturbed ? "_perturbed" : ""}`);
    },

    adjustOvtPerturbedObject(perturbedObjectsList, designator) {
      perturbedObjectsList.splice(perturbedObjectsList.indexOf(designator), 1);
    },

    adjustSovtToolFeatures() {
      const {designator} = this.config.selectedObject;
      const calculatedObjectList = UtilsService.deepCopy(
          SynodicCalculationService.getCalculatedRealSynodicObjectsList()
      );
      const calculatedObjectIndex = UtilsService.findItemIndexInObjectList(
          "designator",
          designator,
          calculatedObjectList
      );
      const {isObjectVisible} = SynodicCalculationService.getSynodicObjectVisibilityInfo(designator);
      if (calculatedObjectIndex === -1) {
        SynodicCalculationService.removePerturbedObjectOrbit(designator);
        return;
      }
      if (isObjectVisible) {
        SynodicCalculationService.removeVisibleCalculatedObject(designator);
      }
      SynodicCalculationService.removePerturbedObjectOrbit(designator);
      calculatedObjectList.splice(calculatedObjectIndex, 1);
      SynodicCalculationService.setCalculatedRealSynodicObjectsList(calculatedObjectList);
    },
  },
};
</script>

<style lang="scss"></style>
